import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';

interface StatusSliderProps<K extends string> {
  steps: { key: K; label: string }[];
  currentStepKey: K;
  onStepChange?: (newKey: K) => void;
  disabled?: boolean;
}

// Simple media-query hook
function useMediaQuery(query: string): boolean {
  const getMatches = (q: string) =>
    typeof window !== 'undefined' ? window.matchMedia(q).matches : false;
  const [matches, setMatches] = useState<boolean>(() => getMatches(query));
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export function StatusSlider<K extends string>({
  steps,
  currentStepKey,
  onStepChange,
  disabled = false,
}: StatusSliderProps<K>) {
  const currentIndex = steps.findIndex((s) => s.key === currentStepKey);
  const sliderValue = currentIndex >= 0 ? currentIndex : 0;

  const labelsRef = useRef<HTMLDivElement>(null);
  const [labelsHeight, setLabelsHeight] = useState<number>(0);
  const isMobile = useMediaQuery('(max-width: 639px)');

  useLayoutEffect(() => {
    const container = labelsRef.current;
    if (container) {
      setLabelsHeight(container.getBoundingClientRect().height);
    }
  }, [steps, isMobile]);

  const handleValueCommit = (value: number[]) => {
    if (onStepChange && steps[value[0]]) {
      const newKey = steps[value[0]].key;
      onStepChange(newKey);
    }
  };

  return (
    <>
      {isMobile ? (
        <div className="flex items-start">
          {/* Vertical slider */}
          <Slider
            orientation="vertical"
            inverted
            min={0}
            max={steps.length - 1}
            step={1}
            value={[sliderValue]}
            disabled={disabled}
            onValueCommit={handleValueCommit}
            className="w-2"
            style={{ height: labelsHeight }}
          />
          {/* Labels are not tappable */}
          <div ref={labelsRef} className="ml-4 flex flex-col space-y-2 h-auto">
            {steps.map((step, i) => (
              <span
                key={step.key}
                className={cn(
                  'text-sm text-left whitespace-nowrap',
                  i === sliderValue ? 'font-semibold text-primary' : 'text-muted-foreground'
                )}
              >
                {step.label}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div className="relative w-full">
          <Slider
            min={0}
            max={steps.length - 1}
            step={1}
            value={[sliderValue]}
            disabled={disabled}
            onValueCommit={handleValueCommit}
            className="w-full h-2"
          />
          <div className="absolute inset-x-0 top-full mt-2 h-0">
            {steps.map((step, i) => (
              <span
                key={step.key}
                style={{ left: `${(i / (steps.length - 1)) * 100}%` }}
                aria-label={`Step ${i + 1} of ${steps.length}: ${step.label}`}
                className={cn(
                  'absolute text-sm transform -translate-x-1/2',
                  i === sliderValue
                    ? 'font-semibold text-primary'
                    : 'text-muted-foreground'
                )}
              >
                {step.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  );
} 